/**
 * Image-editor font catalog + lazy loader.
 *
 * Backed by the platform-resources CDN — fonts are vendored to
 * `cdn.{env}.fias.io` and served as per-family CSS that the loader
 * injects into `document.head` on demand. See `docs/cdn-architecture.md`.
 *
 * Loading model (unchanged from the previous Google-coupled version):
 *  - Each catalog entry carries a `preview` CSS font-family fallback
 *    chain so picker rows render legibly before the `@font-face`
 *    arrives.
 *  - When a user selects a vendored font, `ensureFontLoaded` injects a
 *    `<link rel="stylesheet">` to the platform-resources CDN. The link
 *    is cached by family so re-selection is free.
 *  - System fonts (Arial, Helvetica, …) never trigger a network fetch
 *    — their catalog entries have `vendored: false` and the loader
 *    short-circuits.
 *
 * Public API matches the previous module's shape (renamed without the
 * `Google` prefix) so the picker, text panel, and Fabric integration
 * call sites change only their import paths and function names. The
 * 3s timeout + `document.fonts.load` race semantics are preserved
 * verbatim — see `whenFontReady` below.
 */

import {
  createPlatformResourcesClient,
  normalizeCdnBaseUrl,
  type PlatformResourcesClient,
  type FontEntry as PlatformFontEntry,
} from '@fias/platform-resources';

export type FontCategory = 'sans' | 'serif' | 'display' | 'mono' | 'handwriting' | 'system';

export interface FontEntry {
  /** Value persisted to `TextSettings.fontFamily` AND the family name
   *  shipped to Fabric IText. Must match a CSS font-family name. */
  value: string;
  label: string;
  /** CSS `font-family` string for picker rows + the IText fallback. */
  preview: string;
  category: FontCategory;
  /** `false` for system / OS fonts — loader skips network fetch. */
  vendored: boolean;
}

// Construct the platform-resources client once at module load. Vite's
// `import.meta.env.VITE_PLATFORM_RESOURCES_CDN_DOMAIN` carries the per-env
// CDN URL: `https://cdn.{env}.fias.io` in staging/prod (set by the deploy
// workflow), and the app origin in local dev so the vendored fonts are
// served by the `platform-resources-fonts-dev` Vite middleware (see
// client/.env.development + client/vite.config.ts).
//
// Empty means the CDN isn't configured for this environment — the client
// then emits no font URLs and the picker degrades to system fonts. No
// silent fallback to the prod CDN (staging must never pull prod bytes).
// Resolution is shared with the plugin build/preview services via
// `normalizeCdnBaseUrl` so the surfaces can't drift.
const PLATFORM_RESOURCES_CDN_BASE_URL = normalizeCdnBaseUrl(
  (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
    ?.VITE_PLATFORM_RESOURCES_CDN_DOMAIN,
);

const platformResources: PlatformResourcesClient = createPlatformResourcesClient({
  baseUrl: PLATFORM_RESOURCES_CDN_BASE_URL,
});

/**
 * The catalog the picker shows. Built once from
 * `platformResources.getAvailableFonts()`. System fonts have
 * `vendored: false` so the loader knows to short-circuit.
 *
 * Identical previews to the prior module — categories drive the
 * fallback chain so unloaded faces still render legibly.
 */
export const FONT_CATALOG: readonly FontEntry[] = (() => {
  const entries = platformResources.getAvailableFonts() as readonly PlatformFontEntry[];
  return entries.map((e) => ({
    value: e.value,
    label: e.label,
    preview: rewritePreviewFallback(e),
    category: e.category as FontCategory,
    vendored: e.vendored,
  }));
})();

function rewritePreviewFallback(e: PlatformFontEntry): string {
  // Match the legacy module's fallback chains so picker rows look
  // identical pre- and post-migration. The platform-resources
  // generic-family-only fallback (e.g. "Inter, sans-serif") is
  // adequate but the historical chains include OS fonts as
  // intermediaries, which is mildly nicer on slow networks.
  const quoted = `"${e.value}"`;
  switch (e.category) {
    case 'sans':
      return `${quoted}, system-ui, sans-serif`;
    case 'serif':
      return `${quoted}, Georgia, serif`;
    case 'display':
      return `${quoted}, system-ui, sans-serif`;
    case 'mono':
      return `${quoted}, "Courier New", monospace`;
    case 'handwriting':
      return `${quoted}, cursive`;
    case 'system':
      // Keep the canonical chains for the six OS fonts.
      switch (e.value) {
        case 'Arial':
          return 'Arial, Helvetica, sans-serif';
        case 'Helvetica':
          return 'Helvetica, Arial, sans-serif';
        case 'Georgia':
          return 'Georgia, "Times New Roman", serif';
        case 'Times New Roman':
          return '"Times New Roman", Times, serif';
        case 'Courier New':
          return '"Courier New", Courier, monospace';
        case 'Verdana':
          return 'Verdana, Geneva, sans-serif';
        default:
          return `${quoted}, sans-serif`;
      }
  }
}

/** Filter the catalog by query + category. Case-insensitive substring
 *  match against `label` (query) and exact match against `category`.
 *  `category: 'all'` skips the category filter. Empty query returns
 *  all entries (within the category filter). */
export function filterFonts(
  catalog: readonly FontEntry[],
  query: string,
  category: FontCategory | 'all',
): FontEntry[] {
  const q = query.trim().toLowerCase();
  return catalog.filter((entry) => {
    if (category !== 'all' && entry.category !== category) return false;
    if (q && !entry.label.toLowerCase().includes(q)) return false;
    return true;
  });
}

/** Returns the canonical entry for a `fontFamily` value, or `undefined`
 *  if not in the catalog. Used by `TextPanel` to render the current
 *  selection in its own face in the trigger. */
export function getFontEntry(value: string): FontEntry | undefined {
  return FONT_CATALOG.find((entry) => entry.value === value);
}

/** Per-family link tracker — keys are the canonical family name so
 *  repeated selection of the same font doesn't add duplicate links to
 *  `<head>`. Each entry is a Promise that resolves when the stylesheet
 *  `<link>` has finished loading (so `@font-face` declarations are
 *  parsed and registered with `document.fonts`). Subsequent callers
 *  reuse the same Promise rather than racing. */
const LOADED_FAMILIES = new Map<string, Promise<void>>();

/**
 * Inject the platform-resources CSS for a family if not already
 * injected. No-ops for system fonts (catalog entries with `vendored:
 * false`) and for repeat calls.
 *
 * Side-effect-only — the caller updates `TextSettings.fontFamily`
 * separately; the `@font-face` will swap in once loaded.
 *
 * Safe to call from any context: SSR / Node / happy-dom branches with
 * no `document` fall through silently.
 */
export function ensureFontLoaded(fontFamilyValue: string): void {
  ensureFontStylesheet(fontFamilyValue);
}

/** Internal: inject the `<link>` and return a Promise that resolves
 *  when the stylesheet has finished loading. System fonts and unknown
 *  entries return an immediately-resolved Promise. Memoized via
 *  `LOADED_FAMILIES` so repeat calls share one Promise. */
function ensureFontStylesheet(fontFamilyValue: string): Promise<void> {
  const entry = getFontEntry(fontFamilyValue);
  if (!entry || !entry.vendored) return Promise.resolve();
  const existing = LOADED_FAMILIES.get(entry.value);
  if (existing) return existing;
  if (typeof document === 'undefined') return Promise.resolve();
  const urls = platformResources.getFontCssUrls([entry.value]);
  if (urls.length === 0) {
    // Family not in the manifest yet (sync hasn't run). Resolve
    // immediately — the picker still shows the fallback chain.
    return Promise.resolve();
  }
  const url = urls[0];
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  link.setAttribute('data-image-editor-font', entry.value);
  const ready = new Promise<void>((resolve) => {
    // Both onload and onerror resolve — we don't want a single failed
    // fetch to leave callers awaiting forever. The downstream
    // `document.fonts.load` is itself defensive and falls back to the
    // system font if our family never arrives.
    link.addEventListener('load', () => resolve(), { once: true });
    link.addEventListener('error', () => resolve(), { once: true });
  });
  document.head.appendChild(link);
  LOADED_FAMILIES.set(entry.value, ready);
  return ready;
}

/** How long to wait for a font fetch before falling back to the system
 *  font. Some corporate firewalls, ad-blockers, and offline
 *  environments block the CDN silently — neither the `<link>` `load`
 *  nor `error` event fires. Without a timeout, `whenFontReady`'s
 *  `Promise.all` callers would hang indefinitely. 3s is generous for a
 *  modern network and short enough that an unreachable CDN doesn't
 *  visibly stall session restore.
 *
 *  On timeout the caller sees the same resolution shape as success —
 *  no rejection — so downstream logic continues with whatever font is
 *  currently resolvable (fallback). */
export const FONT_READY_TIMEOUT_MS = 3000;

/** Returns a Promise that resolves once the named font is ready to
 *  render on a canvas2d context (the `@font-face` byte fetch has
 *  completed). Used by the post-`loadFromJSON` rehydration walk in
 *  `useFabricCanvas` to re-init IText dimensions + re-render after the
 *  saved fonts settle — without it, the canvas paints once with a
 *  fallback font (resolves before bytes are in) and then never
 *  re-paints when the bytes land.
 *
 *  Resolves immediately for system fonts (already available) and in
 *  non-browser test environments (no `document.fonts`). Catches
 *  rejections from `document.fonts.load` so a missing / blocked fetch
 *  doesn't poison the caller. Bounded by `FONT_READY_TIMEOUT_MS` so a
 *  blocked CDN can't hang the load. */
export async function whenFontReady(fontFamilyValue: string, sizePx: number = 16): Promise<void> {
  if (typeof document === 'undefined') return;
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  if (!fonts || typeof fonts.load !== 'function') return;
  // STEP 1 — wait for the `<link>` stylesheet to finish fetching.
  // Without this, the `@font-face` declarations may not be registered
  // with `document.fonts` yet, and `document.fonts.load` would resolve
  // immediately with [] (no FontFace matches found) rather than waiting
  // for the bytes. This was the subtle bug where IText rendered against
  // fallback metrics on first load even after the awaited
  // `whenFontReady` "resolved".
  //
  // STEP 2 — once the @font-face decls are registered,
  // `document.fonts.load` resolves with the matched FontFace entries
  // when their bytes land.
  //
  // Both steps race against a single timeout so a blocked CDN doesn't
  // hang the awaited result. The timeout NEVER rejects — both paths
  // resolve so callers can `await` without try/catch.
  await raceWithTimeout(
    (async () => {
      await ensureFontStylesheet(fontFamilyValue);
      try {
        await fonts.load(`${sizePx}px "${fontFamilyValue}"`);
      } catch {
        // Silent — fallback font keeps rendering; not a fatal error.
      }
    })(),
    FONT_READY_TIMEOUT_MS,
  );
}

/** Resolve when `promise` settles OR `ms` elapses, whichever first.
 *  Never rejects. */
function raceWithTimeout(promise: Promise<unknown>, ms: number): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const timer = setTimeout(done, ms);
    promise.finally(() => {
      clearTimeout(timer);
      done();
    });
  });
}

/** Reset the in-memory tracker. Test-only — production has no need to
 *  re-inject a removed link. */
export function _resetLoadedFontsForTests(): void {
  LOADED_FAMILIES.clear();
}

/** Test-only helper: returns true iff a link has been queued for the
 *  given family. */
export function _hasLoadedFontLinkForTests(value: string): boolean {
  return LOADED_FAMILIES.has(value);
}

/** localStorage key for the per-user recent fonts list. Scoped to the
 *  arche so cross-arche conflicts aren't possible. Key value matches
 *  the pre-migration `google-fonts.ts` so users keep their recents. */
export const RECENT_FONTS_STORAGE_KEY = 'image-editor:recent-fonts';

/** Max recents shown in the picker. Mirrors the recent-colors cap. */
export const RECENT_FONTS_MAX = 8;

/** Load recent fonts from localStorage. Returns an empty array on any
 *  parse/storage error so the picker is resilient to a corrupt entry. */
export function loadRecentFonts(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENT_FONTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string').slice(0, RECENT_FONTS_MAX);
  } catch {
    return [];
  }
}

/** Push a font value onto the recents list and persist. Dedupes by
 *  bringing the value to the front; caps at `RECENT_FONTS_MAX`.
 *  Returns the new list (so consumers can avoid a second read). */
export function recordRecentFont(value: string): string[] {
  const current = loadRecentFonts();
  const filtered = current.filter((v) => v !== value);
  const next = [value, ...filtered].slice(0, RECENT_FONTS_MAX);
  if (typeof window === 'undefined') return next;
  try {
    window.localStorage.setItem(RECENT_FONTS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage quota / private mode — silently fall back to the
    // in-memory list. Picker still works for the session.
  }
  return next;
}
